import { useEffect, useState, useRef } from "react";
import AppShell from "@/components/AppShell";
import ReferralCard from "@/components/ReferralCard";
import ServiceCatalog from "@/components/ServiceCatalog";
import RedemptionPanel from "@/components/RedemptionPanel";
import PointsHistory from "@/components/PointsHistory";
import ReferralEntriesList from "@/components/ReferralEntriesList";
import AgilityTierChip from "@/components/AgilityTierChip";
import FirstLoginTour from "@/components/FirstLoginTour";
import AffiliateBankPromptModal from "@/components/AffiliateBankPromptModal";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Banknote, Users, Sparkles, TrendingUp } from "lucide-react";

export default function AffiliateDashboard() {
  const { user, refresh } = useAuth();
  const [refs, setRefs] = useState(null);
  const [open, setOpen] = useState(false);
  const [bank, setBank] = useState({ account_holder: "", account_number: "", ifsc: "", bank_name: "" });
  const bankSectionRef = useRef(null);

  useEffect(() => {
    api.get("/referrals/my").then(r => setRefs(r.data));
    if (user?.bank_details) setBank(user.bank_details);
  }, [user]);

  const saveBank = async () => {
    // Basic validation for accountant-friendly data quality
    const filled = Object.values(bank).every((v) => v && String(v).trim());
    if (!filled) return toast.error("Please fill all four fields");
    if (!/^[A-Z0-9]{6,20}$/i.test((bank.ifsc || "").trim())) return toast.error("Enter a valid IFSC code");
    try {
      await api.post("/auth/bank-details", {
        account_holder: bank.account_holder.trim(),
        account_number: bank.account_number.trim(),
        ifsc: bank.ifsc.trim().toUpperCase(),
        bank_name: bank.bank_name.trim(),
      });
      toast.success("Bank details saved");
      setOpen(false);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Called by the login-nudge modal: scroll the wallet card into view then
  // open the existing "Bank on file" dialog for immediate entry.
  const focusBankSection = () => {
    bankSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    // Small delay so the scroll finishes before the dialog opens on top of it
    setTimeout(() => setOpen(true), 350);
  };

  return (
    <AppShell title={`Welcome, ${user?.name?.split(' ')[0]}`} subtitle="Affiliate control center — higher rewards, cash payouts on demand." badge={<AgilityTierChip />} right={
      <div className="hidden lg:flex items-center gap-2 px-4 py-2 rounded-full bg-pink-500/10 border border-pink-500/30 text-pink-400 text-xs uppercase tracking-wider">
        <TrendingUp className="w-3.5 h-3.5" strokeWidth={2} /> Affiliate tier
      </div>
    }>
      <FirstLoginTour userId={user?.id} />
      <AffiliateBankPromptModal user={user} onGoToBank={focusBankSection} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* PRIMARY ACTIONS on top */}
        <div className="lg:col-span-2 order-1"><ReferralCard onCommitted={() => api.get("/referrals/my").then((r) => setRefs(r.data))} pendingCount={refs?.pending_count ?? 0} maxPending={refs?.max_pending ?? 5} /></div>
        <div ref={bankSectionRef} className="lg:col-span-1 order-2 velo-card p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute -right-10 -bottom-10 w-48 h-48 rounded-full bg-pink-500/10 blur-3xl" />
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Wallet balance</div>
          <div className="flex items-end gap-2 mt-3">
            <div data-testid="points-balance" className="font-mono text-5xl sm:text-6xl text-white font-light tabular-nums">{user?.points_balance ?? 0}</div>
            <div className="text-sm text-slate-400 pb-2">pts</div>
          </div>
          <div className="mt-2 text-sm text-emerald-400 flex items-center gap-2">
            <Sparkles className="w-4 h-4" strokeWidth={2} /> ₹{user?.points_balance ?? 0} redeemable
          </div>

          <div className="mt-5 pt-5 border-t border-white/10 space-y-4">
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="btn-bank-details"
                  type="button"
                  className="w-full h-11 rounded-full bg-pink-500 hover:bg-pink-400 text-black font-medium"
                >
                  <Banknote className="w-4 h-4 mr-2" strokeWidth={1.75} />
                  {user?.bank_details ? "Update bank account details" : "Add bank account details"}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#22103F] border-white/10 text-white max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Bank account for cash payouts</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  {[
                    { k: "account_holder", label: "Account holder name" },
                    { k: "account_number", label: "Account number" },
                    { k: "ifsc", label: "IFSC code" },
                    { k: "bank_name", label: "Bank name" },
                  ].map(f => (
                    <div key={f.k}>
                      <Label className="text-slate-300 text-xs uppercase tracking-wider">{f.label}</Label>
                      <Input data-testid={`input-bank-${f.k}`} value={bank[f.k]} onChange={(e) => setBank(b => ({ ...b, [f.k]: e.target.value }))} className={`velo-input mt-2 h-11 ${f.k.includes('number') || f.k === 'ifsc' ? 'font-mono' : ''}`} />
                    </div>
                  ))}
                </div>
                <DialogFooter>
                  <Button data-testid="btn-save-bank" onClick={saveBank} className="rounded-full bg-pink-500 hover:bg-pink-400 text-black h-11">Save details</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <div className="grid grid-cols-2 gap-4 pt-1">
              <div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wider">Closed referrals</div>
                <div className="text-xl text-white font-light mt-1 flex items-center gap-2"><Users className="w-4 h-4 text-emerald-400" />{refs?.closed_count ?? 0}</div>
              </div>
              <div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wider">Bonus earned</div>
                <div className="text-xl text-white font-light mt-1 font-mono">{refs?.total_referral_bonus ?? 0}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 order-3"><RedemptionPanel role="affiliate" onChange={refresh} /></div>

        {/* SECONDARY */}
        <div className="lg:col-span-2 order-5"><ServiceCatalog role="affiliate" /></div>
        <div className="lg:col-span-1 order-6"><PointsHistory /></div>
        <div className="lg:col-span-3 order-7"><ReferralEntriesList entries={refs?.referral_entries} onChange={() => api.get("/referrals/my").then((r) => setRefs(r.data))} /></div>
      </div>
    </AppShell>
  );
}
